import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import ProductCard from '@components/organisms/catalog/components/product-card/product-card';
import ProductCardSkeleton from '@components/organisms/catalog/components/skeleton-components/product-card-skeleton/product-card-skeleton';
import { Product } from '@interfaces/models/product';
import { CatalogProduct } from '@interfaces/models/catalogProduct';
import { useAuthentication } from '@context/authentication.context';
import { useBucket } from '@context/bucket.context';
import AuthGuard from '@components/atoms/authentication/authentication-guard/authentication-guard';
import ConditionalWrapper from '@components/common/conditional-wrapper/conditional-wrapper';
import { ProductCardProps } from '@components/organisms/catalog/components/product-card/product-card-props';
import useProductFeedImpressionTracking from '@hooks/product-feed/use-product-feed-impression-tracking';
import useOnReveal from '@hooks/use-on-reveal';
import styles from './catalog-product-feed.module.scss';

type CatalogProductFeedProps = {
  products?: Array<Product | CatalogProduct>;
  isLoading?: boolean;
  containerClsx?: React.CSSProperties | string;
  productCardProps?: Omit<ProductCardProps, 'product' | 'index'>;
};

type ImpressionTrackingWrapperProps = {
  index: number;
  productsOnRow: Product[];
  onRowReveal: (index: number, productsOnRow: Product[]) => void;
  children: React.ReactNode;
};

const ImpressionTrackingWrapper: React.FC<ImpressionTrackingWrapperProps> = ({
  index,
  productsOnRow,
  onRowReveal,
  children,
}) => {
  const { containerRef } = useOnReveal(() => onRowReveal(index, productsOnRow), 1, false);

  return (
    <li
      ref={containerRef}
      className={styles['product_feed__flexContainer--item']}
    >
      {children}
    </li>
  );
};

const CatalogProductFeed: React.FC<CatalogProductFeedProps> = ({
  products,
  isLoading,
  containerClsx,
  productCardProps,
}): React.JSX.Element => {
  const { t } = useTranslation();
  const { shouldDisplayAuthDialog, setShouldDisplayAuthDialog, setRedirectUrl } = useAuthentication();

  const { isFeatureEnabled } = useBucket();
  const isGuestXpPdp = isFeatureEnabled((f) => f.guestXpPdp, true);
  const productFeedCategory = productCardProps?.productFeedCategory;
  const trackingPayload =
    productCardProps?.cmsProps && productCardProps?.pageType
      ? {
          cms_block_id: productCardProps.cmsProps.blockUid,
          contentTypeUid: productCardProps.cmsProps.contentTypeUid,
          productType: productCardProps.cmsProps.productType,
          pageType: productCardProps.pageType,
          product_feed_category: productFeedCategory,
        }
      : { product_feed_category: productFeedCategory };

  const { onRowReveal, rowCount, hasWindowSizeBeenSet } = useProductFeedImpressionTracking(
    productFeedCategory,
    trackingPayload,
  );

  const productList = useMemo(() => {
    if (isLoading) {
      return Array.from(Array(5).keys()).map((key) => (
        <li
          key={key}
          className={styles['product_feed__flexContainer--item']}
        >
          <ProductCardSkeleton />
        </li>
      ));
    }

    if (!products?.length) {
      return null;
    }

    const typedProducts = products as Product[];

    return products?.map((product, index) => {
      const content = (
        <ConditionalWrapper
          condition={!isGuestXpPdp && shouldDisplayAuthDialog}
          wrap={(children) => (
            <AuthGuard
              onClick={() => {
                setShouldDisplayAuthDialog(false);
                setRedirectUrl({ url: product.path, mode: 'push' });
              }}
              shouldPreventDefault={true}
              onAuth={() => (window.location.pathname = product.path)}
            >
              {children}
            </AuthGuard>
          )}
        >
          <ProductCard
            index={index}
            // TODO: fix typings to allow for CatalogProduct
            product={product}
            {...productCardProps}
            customClass={styles.productCardContainer}
          />
        </ConditionalWrapper>
      );

      const isFirstItemOnRow = hasWindowSizeBeenSet && index % rowCount === 0;

      if (isFirstItemOnRow) {
        const productsOnRow = typedProducts.slice(index, index + rowCount);
        return (
          <ImpressionTrackingWrapper
            key={product.id}
            index={index}
            productsOnRow={productsOnRow}
            onRowReveal={onRowReveal}
          >
            {content}
          </ImpressionTrackingWrapper>
        );
      }

      return (
        <li
          key={product.id}
          className={styles['product_feed__flexContainer--item']}
        >
          {content}
        </li>
      );
    });
  }, [
    isLoading,
    products,
    productCardProps,
    isGuestXpPdp,
    shouldDisplayAuthDialog,
    hasWindowSizeBeenSet,
    rowCount,
    onRowReveal,
  ]);

  return (
    <div className={clsx(styles.product_feed__column, containerClsx)}>
      {!productList ? (
        <span>{t('CATALOG.NO_RESULTS')}</span>
      ) : (
        <ul className={styles.product_feed__flexContainer}>{productList}</ul>
      )}
    </div>
  );
};

export default CatalogProductFeed;
